import React, { useRef, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as Sentry from '@sentry/browser';
// eslint-disable-next-line import/no-extraneous-dependencies
import fixWebmDuration from 'webm-duration-fix';
import { ToastNotification, NOTIFICATION_TYPES } from '../ToastNotification';
import RecorderButtons from './RecorderButtons';
import RecorderProcessors from './RecorderProcessors';
import RecorderVideoPlayer from './RecorderVideoPlayer';
import Recorder from './Recorder';
import RecorderPotentialMsg from './RecorderPotentialMsg';
import useTranslations from './hooks/useTranslations';
import { getBlobDuration } from './utils/blobDurationSelector';
import { resizeRecorder } from './utils/resizeRecorderSelector';
import { setStream } from './utils/recorderStream';
import {
    resolutions, internalResolutions, filteredResolutions, RECORDER_STATE,
} from './RecorderConstants';
import { setScreenStream } from './utils/screenStream';
import { blurEffect, renderBackgroundImg } from './utils/virtualBgFunctions';
import { buildBackgroundEffectsFallbackState } from './utils/backgroundEffectsFallback';


// Define global variables
let latestVideoId; let bgImg = ''; let backgroundBlurAmount = ''; let backgroundImg; let isBgEnabled = false; let previewElem; let canvas; let ctx; let net; let recording = false; let combinedStream; let backgroundCanvas; let backgroundCtx; let backgroundImageData = null; let personImageCanvas; let personImageCtx; let personImageData = null; let
    selectedInternalRes;

// RecorderService component
const RecorderService = ({
    videoId,
    libraryVideoId,
    initializeWebcam,
    recorderState,
    isVideoAvailable,
    videoSource,
    animatedThumbPath,
    recorderOptions,
    isUploadVideo,
    streamType,
    portalHasVirtualBackgrounds,
    selectedThumbnail,
    saveVideo = () => { },
    closeWebcam = () => { },
    updateVideoStatus = () => { },
    toggleInstructionOverlay = () => { },
}) => {
    // Refs for DOM elements
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const containerRef = useRef();
    const fileInputRef = useRef(null);

    // Initialize latestVideoId and selectedInternalRes
    latestVideoId = videoId;
    selectedInternalRes = internalResolutions.find((res) => res.selected).label;

    // UseReducer for local state management
    const [localState, setLocalState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
        ...RECORDER_STATE, videoStatus: 'waiting', selectedResolution: resolutions[0].value, selectedInternalResolution: selectedInternalRes,
    });

    // Destructure local state
    const {
        cameraRecorder, uploadPercentage, webmMimeType, recorderParams, videoStatus, fontSize, selectedResolution, selectedInternalResolution, recordingLimit, recordingTime, audioDevices, cameraDevices, isVidAvailable, isScreenRecording, screenStream, screenRecorder, backgrounds, cameraStream, bgEffectsUnavailable, bgEffectsErrorCount,
    } = localState;

    // Translations hook
    const { trans } = useTranslations();



    // Hook to initialize webcam and canvas
    useEffect(() => {
        if (initializeWebcam) {
            previewElem = videoRef.current;
            // Create canvas and context
            canvas = canvasRef.current;
            ctx = canvas.getContext('2d', { willReadFrequently: true });
            backgroundCanvas = document.createElement('canvas');
            personImageCanvas = document.createElement('canvas');
            // updateVideoStatus('beginRecording'); Uncomment this if the recorder breaks at the beginning while enabling the webcam, and comment out the "beginRecording" status update in recorderStream.jsx
            callStream(selectedResolution);
        }
    }, [initializeWebcam]);

    // Hook to handle upload video
    useEffect(() => {
        if (isUploadVideo) {
            handleUploadClick();
        }
    }, [isUploadVideo]);

    // Hook to update video availability
    useEffect(() => {
        if (latestVideoId) {
            isVidAvailable[latestVideoId] = isVideoAvailable;
            setLocalState({
                isVidAvailable,
            });
        }
    }, [latestVideoId]);



    // Hook to handle recorder state changes
    useEffect(() => {
        if (recorderState) {
            document.querySelector('.user-block').disabled = recorderState === 'activatingCamera'; // fix for the condition: click + when previous record is in activatingCamera state. We can remove this line once personalVideo.js will be converted into Reactjs from Angular
            setLocalState({
                videoStatus: recorderState,
            });
            if (recorderState === 'recording' && !isScreenRecording) {
                if (cameraRecorder) {
                    cameraRecorder.startRecording();
                }
            }
            if (recorderState !== 'waiting') {
                // Initial resize on component mount
                resize();
                // Event listener for window resize
                window.addEventListener('resize', resize);
                return () => {
                    // Clean up the event listener when the component unmounts
                    window.removeEventListener('resize', resize);
                };
            }
        }
    }, [recorderState]);

    // Hook to handle screen recording
    useEffect(() => {
        if (screenRecorder && videoStatus && isScreenRecording) {
            if (videoStatus === 'recording') {
                screenRecorder.startRecording();
                if (screenStream) {
                    // Check if screenStream is not null or undefined
                    screenStream.getTracks().forEach((track) => {
                        track.addEventListener('ended', handleStopScreenRecording);
                    });
                }
            }
        }
        return () => {
            // Cleanup: remove event listeners to avoid memory leaks
            if (screenStream) {
                screenStream.getTracks().forEach((track) => {
                    track.removeEventListener('ended', handleStopScreenRecording);
                });
            }
        };
    }, [screenRecorder, isScreenRecording, videoStatus]);

    // Function to resize the recorder
    const resize = () => {
        const maxHeight = window.innerHeight * 0.8;
        const updatedFontSize = resizeRecorder({ containerRef, maxHeight });
        setLocalState({
            fontSize: updatedFontSize,
        });
    };

    // Function to handle recording start
    const handleRecordingStart = () => {
        if (streamType === 'camera') {
            updateVideoStatus('recordingCountdown');
        } else {
            previewElem.style.display = 'block';
            handleStartScreenRecording();
        }
    };

    // Function to handle starting over the recording
    const handleStartOver = () => {
        // Reset cameraRecorder if it's not null
        if (cameraRecorder) {
            cameraRecorder.stopRecording(); // Stop the recording if it's in progress
            cameraRecorder.destroy(); // Destroy the cameraRecorder instance
        }
        resetRecorder();
        // Initialize a new cameraRecorder
        callStream(selectedResolution);
        updateVideoStatus('beginRecording');
    };

    // Function to handle re-recording
    const handleReRecord = () => {
        isVidAvailable[latestVideoId] = false; // Set the latest video as unavailable
        // Update local state to reflect the change in video availability
        setLocalState({
            isVidAvailable,
        });
        // Update video status to 'waiting' to prepare for a new recording
        updateVideoStatus('waiting');
    };

    // Function to handle upload progress
    const handleProgress = (e) => {
        if (e.event.lengthComputable) {
            // Calculate and update upload percentage
            const newPercentage = Math.floor((e.event.loaded * 100) / e.event.total);
            setLocalState({
                uploadPercentage: newPercentage,
            });
        }
    };

    // Function to handle successful video upload
    const handleSuccess = (response, id) => {
        if (response.data.success) {
            // Finished uploading video & process uploaded video data
            const video = response.data.video;
            video.video_type = 'webcam';
            video.start_trim = 0;
            video.video_thumb = video.video_thumbs[0];
            // Update video availability status
            if (id === latestVideoId) {
                isVidAvailable[latestVideoId] = id === latestVideoId;
            }
            // Update local state to reflect the change in video availability
            setLocalState({ isVidAvailable });
            // Save the uploaded video
            saveVideo(video, id);
        } else {
            // Handle upload failure
            updateVideoStatus('uploadFailed');
            console.log('uploadFailed');
        }
    };

    // Function to handle upload error
    const handleError = () => {
        updateVideoStatus('uploadFailed');
        console.log('uploadFailed');
    };

    // Function to upload the recorded video
    const uploadRecording = (formData, id) => {
        // Reset recorder state
        resetRecorder();
        // Construct upload URL
        const url = config.mediaUrl + '/api/upload/video';
        // Update video status to indicate uploading
        updateVideoStatus('uploading');
        // Perform the upload operation
        axios.post(url, formData, {
            onUploadProgress: handleProgress,
        }).then((response) => {
            // Update video status upon successful upload
            if (id === latestVideoId) {
                updateVideoStatus('beginReview');
            }
            handleSuccess(response, id);
        })
            .catch(handleError);
    };

    // Function to handle recording blob data and initiate upload
    const handleRecording = (blob, duration, id) => {
        // Determine file name based on MIME type
        const fileName = webmMimeType ? 'blob.webm' : 'blob.mp4';
        // Create a File object from the blob data
        const fileObject = new window.File([blob], fileName, {
            type: recorderParams.mimeType,
        });
        // Create a FormData object to send the file data
        const formData = new FormData();
        formData.append('file', fileObject);
        formData.append('id', config.user.id);
        // Append video duration if available
        if (duration) {
            formData.append('video_length', duration);
        }
        // Upload the recording data
        uploadRecording(formData, id);
    };

    // Function to handle file upload from the user's device
    const handleFileUpload = (e) => {
        // Get the selected file from the input element
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Create a FormData object to send the file data
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('id', config.user.id);
            // Upload the file data
            uploadRecording(formData, videoId);
        }
    };

    // Function to handle recording stop
    const handleStopRecording = () => {
        // Determine the type of recording (camera or screen) and take appropriate action
        if (streamType === 'screen') handleStopScreenRecording();
        else {
            // Stop camera recording
            if (cameraRecorder) {
                cameraRecorder.stopRecording(() => {
                    saveRecording(cameraRecorder.getBlob());
                });
            }
            // Pause video playback
            if (videoRef.current) {
                videoRef.current.pause();
            }
            // Close webcam
            closeWebcam();
        }
        // Toggle instruction overlay
        toggleInstructionOverlay('', true);
        // Stop virtual background processing if enabled
        if (isBgEnabled) {
            recording = false;
            previewElem.removeEventListener('loadeddata', loadedDataHandler);
        }
        // Reset upload percentage
        setLocalState({
            uploadPercentage: 0,
        });
    };

    // Function to update recording time limit and stop recording if exceeded
    const updateRecordingTimeLimit = (time, limit = recordingLimit) => {
        const timeSeconds = time % 60;
        const limitSeconds = limit % 60;
        const updatedTime = Math.floor(time / 60) + ':' + (timeSeconds < 10 ? '0' : '') + (timeSeconds % 60);
        const updatedLimit = Math.floor(limit / 60) + ':' + (limitSeconds < 10 ? '0' : '') + (limitSeconds % 60);
        if (time >= limit) {
            // Stop recording if time limit exceeded
            handleStopRecording();
        }
        // Update local state with updated time and limit
        setLocalState({
            recordingLimit: updatedLimit,
            recordingTime: updatedTime,
        });
    };

    // Function to set recording resolution
    const setResolution = (resolution) => {
        // Deselect all resolutions and select the chosen one
        resolutions && resolutions.map((option) => {
            option.selected = false;
            return option;
        });
        resolution.selected = true;
        // Update local state with selected resolution
        setLocalState({
            selectedResolution: resolution.value,
        });
        // Reset recorder and initiate new stream with the chosen resolution
        resetRecorder();
        callStream(resolution.value);
    };

    // Function to set internal resolution (for virtual background processing)
    const setInternalResolution = (resolution) => {
        // Deselect all internal resolutions and select the chosen one
        internalResolutions.map((option) => {
            option.selected = false;
            return option;
        });
        resolution.selected = true;
        selectedInternalRes = resolution.label;
        // Update local state with selected internal resolution
        setLocalState({
            selectedInternalResolution: selectedInternalRes,
        });
    };

    // Function to set available camera and audio devices
    const setDevices = (cameraDevices, audioDevices) => {
        // Update local state with available camera and audio devices
        setLocalState({
            cameraDevices,
            audioDevices,
        });
    };

    // Function to stop a single stream
    const stopStream = (stream) => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
        }
    };

    // Function to reset the recorder
    const resetRecorder = () => {
        if (streamType === 'camera') {
            if (isBgEnabled) {
                stopStream(combinedStream);
            }
        } else {
            stopStream(screenStream);
        }
        stopStream(cameraStream);
        combinedStream = null;
        // Reset local state for camera and screen streams
        setLocalState({ cameraStream: null, screenStream: null });
        recording = false;
    };

    // Function to initiate video streaming
    const callStream = async (resolution = selectedResolution, selectedCameraId, selectedAudioId) => {
        // Check if the video reference is available
        if (!videoRef.current) return;
        if (isBgEnabled && !net) {
            try {
                // Load the BodyPix model if not already loaded dynamically
                await import('@tensorflow/tfjs-backend-webgl');
                const bodyPix = await import('@tensorflow-models/body-pix');
                net = await bodyPix.load({
                    architecture: 'MobileNetV1',
                    outputStride: 16,
                    multiplier: 0.75,
                    quantBytes: 2,
                });
            } catch (error) {
                // Background effects can fail to load (e.g. WebGL unavailable, or the
                // model host is unreachable on a restrictive network), which used to leave
                // the preview on a dead camera stream with no feedback. Fall back to a
                // plain camera stream instead of leaving the user stuck on a black preview.
                console.error('Failed to load background effects model:', error);
                Sentry.captureException(error);
                if (!isBgEnabled) {
                    // The user already turned background effects fully off while the model
                    // was loading; don't clobber their current (disabled) selection with
                    // feedback for a stale, superseded attempt. Note: this doesn't catch every
                    // race - e.g. switching from Blur to Virtual Background mid-load leaves
                    // isBgEnabled true, so a stale failure can still reset the newer selection.
                    return;
                }
                const fallbackState = buildBackgroundEffectsFallbackState(backgrounds, bgEffectsErrorCount);
                isBgEnabled = fallbackState.isBgEnabled;
                bgImg = fallbackState.bgImg;
                backgroundBlurAmount = fallbackState.backgroundBlurAmount;
                setBackgrounds(fallbackState.backgrounds);
                setLocalState({ bgEffectsUnavailable: fallbackState.bgEffectsUnavailable, bgEffectsErrorCount: fallbackState.bgEffectsErrorCount });
            }
        }
        recording = true;
        // Show or hide preview element based on virtual background status
        previewElem.style.display = isBgEnabled ? 'none' : 'block';
        // Set up video streaming
        setStream(resolution, selectedCameraId, selectedAudioId, recorderParams, videoRef, canvas, setResolution, updateVideoStatus, setDevices, combinedStream, isBgEnabled, applyVirtualBackground)
            .then((result) => {
                if (!result) return;
                combinedStream = result.combinedStream;
                // Update local state with camera recorder, parameters, and stream
                setLocalState({
                    cameraRecorder: result.cameraRecorder,
                    recorderParams: result.recorderParam,
                    webmMimeType: result.webmMimeType,
                    cameraStream: result.cameraStream,
                });
            })
            .catch((error) => {
                // Handle any errors that occur during camera recording setup
                console.error(error);
            });
    };

    // Function to trigger file input click event before upload
    const handleUploadClick = () => {
        fileInputRef.current.value = ''; // Clear the previous file selection before triggering click event
        // Trigger the click event on the file input element
        fileInputRef.current.click();
    };

    // Function to start screen recording
    const handleStartScreenRecording = () => {
        // Check if the video reference is available
        if (!videoRef.current) return;
        // Set up screen stream and recording
        setScreenStream(recorderParams, videoRef, updateVideoStatus)
            .then((result) => {
                // If no result, return
                if (!result) return;
                // Reset recorder before setting new state
                resetRecorder();
                // Set local state with screen recording details
                setLocalState({
                    screenRecorder: result.screenRecorder,
                    recorderParams: result.recorderParam,
                    webmMimeType: result.webmMimeType,
                    isScreenRecording: result.isScreenRecording,
                    screenStream: result.screenStream,
                });
            })
            .catch((error) => {
                // Handle any errors that occur during screen recording setup
                console.error(error);
            });
    };

    // Function to stop screen recording
    const handleStopScreenRecording = () => {
        // Check if screenStream exists
        if (screenStream) {
            screenStream.getTracks().forEach((track) => track.stop()); // Stop all tracks in the screenStream
        }
        // Check if screenRecorder exists
        if (screenRecorder) {
            // Stop the screen recording
            screenRecorder.stopRecording(() => {
                // Call saveScreenRecording to process or save the recorded content
                saveRecording(screenRecorder.getBlob());
            });
            // Set local state to indicate screen recording has stopped
            setLocalState({
                isScreenRecording: false,
            });
        }
    };

    // Function to save the recording
    const saveRecording = (blob) => {
        // Check if the recording is in webm format
        if (webmMimeType) {
            // Webm videos don't have the correct metadata, so fix the duration
            // Use below functions to add the duration to the video
            getBlobDuration({ blob }).then((duration) => {
                // Use webm-duration-fix to fix the video metadata without 10MB memory corruption
                fixWebmDuration(blob).then((fixedBlob) => {
                    handleRecording(fixedBlob, duration, videoId);
                }).catch(() => {
                    // Fallback to original blob if fixing fails
                    handleRecording(blob, duration, videoId);
                });
            });
        } else {
            // If the recording is not in webm format, call handleRecording with the original blob
            handleRecording(blob, '', videoId);
        }
    };

    // Function to handle background blur settings
    const handleBlur = (val) => {
        // Reset the recorder
        resetRecorder();
        // Set the background blur amount
        backgroundBlurAmount = val;
        // Clear the background image
        bgImg = '';
        // Enable or disable isBgEnabled variable based on the blur amount
        isBgEnabled = !['', 0].includes(val);
        // Set the selected resolution(480p)
        setLocalState({ selectedResolution: filteredResolutions[0].value });
        // If the blur amount is 0, start streaming with the selected resolution
        if (val === 0) {
            callStream(selectedResolution);
        }
        // If no combined stream exists, start streaming with the default resolution(480p)
        if (!combinedStream) callStream(filteredResolutions[0].value);
    };

    // Function to handle background image selection
    const handleImage = (img) => {
        try {
            // Reset the recorder
            resetRecorder();
            // Clear the canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // Set the background image
            bgImg = img;
            // Reset background blur amount
            backgroundBlurAmount = '';
            // Create a new image element and set its source to the selected image
            backgroundImg = new Image();
            backgroundImg.src = bgImg;
            // Get the context of the background canvas
            backgroundCtx = backgroundCanvas.getContext('2d', { willReadFrequently: true });
            // Draw the background image on the background canvas
            backgroundCtx.drawImage(backgroundImg, 0, 0, backgroundCanvas.width, backgroundCanvas.height);
            // Reset background image data
            backgroundImageData = null;
            // Set the selected resolution(480p)
            setLocalState({ selectedResolution: filteredResolutions[0].value });
            // Enable isBgEnabled variable if an image is selected
            isBgEnabled = bgImg !== '';
            // If no combined stream exists, start streaming with the default resolution(480p)
            if (!combinedStream) callStream(filteredResolutions[0].value);
        } catch (error) {
            console.error('Error converting image to Base64:', error);
        }
    };

    // Wait for the video element to signal it actually has frame data, then retry -
    // event-driven so we don't burn a requestAnimationFrame tick every ~16ms while waiting
    const retryProcessFrameWhenReady = () => {
        const retry = () => {
            previewElem.removeEventListener('canplay', retry);
            previewElem.removeEventListener('playing', retry);
            previewElem.removeEventListener('timeupdate', retry);
            processFrame();
        };
        previewElem.addEventListener('canplay', retry, { once: true });
        previewElem.addEventListener('playing', retry, { once: true });
        previewElem.addEventListener('timeupdate', retry, { once: true });
    };

    // Function to process each frame of the video stream
    const processFrame = async () => {
        // Only draw/segment once the video preview element has enough data ready -
        // if not, wait for it to become ready instead of dropping the loop
        if (previewElem.readyState === 4) {
            // Get the 2D rendering context for the personImageCanvas
            personImageCtx = personImageCanvas.getContext('2d', { willReadFrequently: true });
            // Draw the current frame from the video onto the personImageCanvas
            personImageCtx.drawImage(previewElem, 0, 0, personImageCanvas.width, personImageCanvas.height);

            // Get the image data of the frame from the personImageCanvas
            personImageData = personImageCtx.getImageData(0, 0, personImageCanvas.width, personImageCanvas.height);

            // Use the neural network to segment the person from the frame
            const cachedPersonSegmentation = await net.segmentPerson(personImageData, {
                flipHorizontal: false,
                internalResolution: selectedInternalRes,
                segmentationThreshold: 0.5,
            });
            // Apply background effects based on whether a background image is set
            if (bgImg) {
                // Render the background image with the segmented person onto the canvas
                renderBackgroundImg(cachedPersonSegmentation, backgroundImg, backgroundImageData, backgroundCtx, ctx, personImageData);
            } else {
                // Apply blur effect to the canvas based on the person segmentation
                blurEffect(canvas, previewElem, cachedPersonSegmentation, backgroundBlurAmount);
            }
            // Request the next frame if recording is ongoing
            if (recording) {
                requestAnimationFrame(processFrame);
            }
        } else if (recording) {
            retryProcessFrameWhenReady();
        }
    };

    // Event handler function for when video metadata is loaded
    const loadedDataHandler = () => {
        // Check if background is enabled
        if (!isBgEnabled) return;
        // Process the frame for background effects
        processFrame();
    };

    // Function to apply virtual background
    const applyVirtualBackground = () => {
        // Set dimensions for video and canvas elements
        const width = 640;
        const height = 480;
        Object.assign(previewElem, { width, height });
        Object.assign(canvas, { width, height });
        Object.assign(personImageCanvas, { width, height });
        Object.assign(backgroundCanvas, { width, height });
        // Add event listener to handle loaded data for video element
        previewElem.addEventListener('loadeddata', loadedDataHandler);
    };

    // Function to set available backgrounds
    const setBackgrounds = (backgrounds) => {
        // Update local state with provided backgrounds
        setLocalState({
            backgrounds,
        });
    };

    console.log('status', videoStatus);

    // Render the main components of the recorder interface
    return (
        <>
            {/* Container for the recorder */}
            <div ref={containerRef} className={`tv-recorder ${recorderState !== 'waiting' ? 'relative' : ''} margin-center`} style={{ fontSize }}>
                {/* Recorder component for controlling recording */}
                <Recorder status={videoStatus} streamType={streamType} selectedResolution={selectedResolution} setResolution={setResolution} setInternalResolution={setInternalResolution} portalHasVirtualBackgrounds={portalHasVirtualBackgrounds} isBgEnabled={isBgEnabled} recordingTime={recordingTime} recordingLimit={recordingLimit} setStream={callStream} audioDevices={audioDevices} cameraDevices={cameraDevices} isWebcamOpen={initializeWebcam} videoRef={videoRef} canvasRef={canvasRef} fileInputRef={fileInputRef} handleFileUpload={handleFileUpload} handleBlur={handleBlur} handleImage={handleImage} backgrounds={backgrounds} setBackgrounds={setBackgrounds} resetRecorder={resetRecorder} />

                {/* Recorder control buttons */}
                <RecorderButtons status={videoStatus} trans={trans} isWebcamOpen={initializeWebcam} recorderOptions={recorderOptions} toggleInstructionOverlay={toggleInstructionOverlay} startRecording={handleRecordingStart} stopRecording={handleStopRecording} startOverRecording={handleStartOver} reRecordRecording={handleReRecord} handleUploadClick={handleUploadClick} />

                {/* Video player for recorded content */}
                <RecorderVideoPlayer status={videoStatus} isSelectedVideoAvailable={isVidAvailable[latestVideoId]} libraryVideoId={libraryVideoId} videoSource={videoSource} animatedThumbPath={animatedThumbPath} selectedThumbnail={selectedThumbnail} updateStatus={updateVideoStatus} />

                {/* Processing indicators and progress */}
                <RecorderProcessors status={videoStatus} percentage={uploadPercentage} videoSource={videoSource} trans={trans} updateRecordingTimeLimit={updateRecordingTimeLimit} updateStatus={updateVideoStatus}
                />
            </div>

            {/* Display potential messages based on recorder status */}
            {['activatingCamera', 'cameraDenied', 'beginRecording', 'recording'].includes(videoStatus) && <RecorderPotentialMsg />}

            {/* Background effects (blur/virtual background) failed to load; recording continues
                without them. Keyed on bgEffectsErrorCount so a fresh failure re-shows the toast
                even if the user already dismissed an earlier one. */}
            <ToastNotification
                key={bgEffectsErrorCount}
                isVisible={bgEffectsUnavailable}
                notificationType={NOTIFICATION_TYPES.ERROR}
                msg={trans['bg-effects-unavailable'] || "Background effects aren't available right now, so recording will continue without them."}
            />
        </>
    );
};

RecorderService.propTypes = {
    videoId: PropTypes.number,
    libraryVideoId: PropTypes.number,
    initializeWebcam: PropTypes.bool,
    isVideoAvailable: PropTypes.bool,
    isUploadVideo: PropTypes.bool,
    videoSource: PropTypes.string,
    recorderState: PropTypes.string,
    animatedThumbPath: PropTypes.string,
    streamType: PropTypes.string,
    recorderOptions: PropTypes.object,
    selectedThumbnail: PropTypes.string,
    portalHasVirtualBackgrounds: PropTypes.bool,
    closeWebcam: PropTypes.func,
    saveVideo: PropTypes.func,
    updateVideoStatus: PropTypes.func,
    toggleInstructionOverlay: PropTypes.func,
};

export default RecorderService;
